/**
 * Which alert chips (the backend's rule chips: overdue reminders, an expired
 * passport) the user has dismissed on a dashboard, kept in localStorage per
 * scope (the client id) so a dismissed alert stays gone after a reload. An
 * alert is keyed by its id *and* its text, so when the message changes ("3
 * overdue reminders" becomes "4") it is a new alert and shows again. Every
 * storage access is guarded: a blocked or full store just means the dismissal
 * lasts until the page reloads.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import type { Recommendation } from "./types";

export const DISMISSED_ALERTS_KEY = (scopeId: string) =>
  `dashboard.alerts.dismissed.${scopeId}`;
/** Only the most recent dismissals are kept, so the list never grows without bound. */
export const DISMISSED_ALERTS_CAP = 200;

/** Whether a chip is an alert (and so can be dismissed). */
export const isAlert = (rec: Recommendation): boolean => rec.source === "rule";

/** The key an alert is dismissed under: it changes when its text does. */
export const alertKey = (rec: Pick<Recommendation, "id" | "title">): string =>
  `${rec.id}::${rec.title}`;

const read = (key: string): string[] => {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((k): k is string => typeof k === "string")
      : [];
  } catch {
    return [];
  }
};

const write = (key: string, value: string[]) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage is blocked or full; the dismissal lasts for this page view.
  }
};

export type DismissedAlerts = {
  /** The chips without the dismissed alerts; curated chips always pass. */
  visible: (items: readonly Recommendation[]) => Recommendation[];
  dismiss: (rec: Recommendation) => void;
};

/** `scopeId` null turns dismissal off (nothing is hidden, `dismiss` is a no-op). */
export const useDismissedAlerts = (scopeId: string | null): DismissedAlerts => {
  const storageKey = scopeId ? DISMISSED_ALERTS_KEY(scopeId) : null;
  const [keys, setKeys] = useState<string[]>(() =>
    storageKey ? read(storageKey) : []
  );

  useEffect(() => {
    setKeys(storageKey ? read(storageKey) : []);
  }, [storageKey]);

  const dismissed = useMemo(() => new Set(keys), [keys]);

  const visible = useCallback(
    (items: readonly Recommendation[]) =>
      items.filter((r) => !(isAlert(r) && dismissed.has(alertKey(r)))),
    [dismissed]
  );

  const dismiss = useCallback(
    (rec: Recommendation) => {
      if (!storageKey || !isAlert(rec)) return;
      const key = alertKey(rec);
      setKeys((prev) => {
        if (prev.includes(key)) return prev;
        const next = [...prev, key].slice(-DISMISSED_ALERTS_CAP);
        write(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  return { visible, dismiss };
};
